import { useCallback, useEffect, useRef, useState } from 'react';
import EditorWrapper from '../../editor/EditorWrapper';
import { TiptapMenuType } from '../../editor/utils';
import { TiptapOutputType, getHTML, getText } from '@amzn/awscat-editor';

interface SimpleRichTextInputProps {
  /** Initial/reset value. Only used at mount and when clearing (see resetKey below). */
  value: string;
  onChange: (html: string) => void;
  onCharacterCountChange?: (count: number) => void;
  disabled?: boolean;
}

/**
 * Minimal rich text editor (bold, code, link, clear formatting).
 *
 * EditorWrapper is uncontrolled after mount, so `value` only applies on mount.
 * To clear the editor, the parent sets value to '' — a useEffect detects the
 * transition and bumps an internal key to force a remount.
 */
const SimpleRichTextInput = ({
  value,
  onChange,
  onCharacterCountChange,
  disabled,
}: SimpleRichTextInputProps) => {
  const [resetKey, setResetKey] = useState(0);
  const prevValue = useRef(value);

  // Detect value clearing (non-empty → empty) and remount the editor
  useEffect(() => {
    if (!value && prevValue.current) {
      setResetKey((k) => k + 1);
      onCharacterCountChange?.(0);
    }
    prevValue.current = value;
  }, [value, onCharacterCountChange]);

  const handleModelChange = useCallback(
    (newModel: TiptapOutputType) => {
      const html = typeof newModel === 'string' ? newModel : getHTML(newModel);
      onCharacterCountChange?.(getText(newModel).length); // plain-text count, same as MetadataEditorInputV2
      onChange(html);
    },
    [onChange, onCharacterCountChange],
  );

  return (
    <div className="rich-text-comment-input">
      <EditorWrapper
        key={resetKey}
        model={value}
        readOnly={false}
        onModelChange={handleModelChange}
        menuType={TiptapMenuType.Simple}
        disabled={disabled}
        hideFooter
      />
    </div>
  );
};

export default SimpleRichTextInput;
